// ======================================================================
// File: src/modules/ppe/pages/procurement/grn/form/GRNFormDrawer.jsx
// Module: PPE → Procurement → GRN
//
// Purpose:
// - Drawer component for creating GRN header
// - Uses searchable dropdowns for Vendor / PO
// - Temporary numeric input for Warehouse ID (until module active)
// ======================================================================

import React, { useState, useEffect } from "react";
import grnService from "../services/grnService";
import "../grn-ui.css";

import SearchableDropdown from "../../../../components/common/SearchableDropdown";
import warehouseLookupService from "../services/warehouseLookupService";

import vendorLookupService from "../services/vendorLookupService";
import poLookupService from "../services/poLookupService";

export default function GRNFormDrawer({
  open,
  mode = "create",
  record,
  onClose,
  onSaved,
}) {

  //const [form, setForm] = useState({
  //  grn_date: "",
  const today = new Date().toISOString().split("T")[0];

const [form, setForm] = useState({
  grn_date: today,

    vendor_id: "",
    vendor_label: "",

    warehouse_id: "",   
    warehouse_label: "",
    po_id: "",
    po_label: "",

    invoice_no: "",
    invoice_date: "",
    vehicle_no: "",
    lr_no: "",
    remarks: "",
  });

  const [saving, setSaving] = useState(false);
  //const [warehouseOptions, setWarehouseOptions] = useState([]);
  const isEdit = mode === "edit";

  // ----------------------------------------------------------
  // PREFILL FOR EDIT MODE
  // ----------------------------------------------------------
  useEffect(() => {
    if (record) {
      setForm({
        grn_date: record.grn_date || "",

        vendor_id: record.vendor_id || "",
        vendor_label: record.vendor_name || "",

        warehouse_id: record.warehouse_id || "",

        po_id: record.po_id || "",
        po_label: record.po_no || "",

        invoice_no: record.invoice_no || "",
        invoice_date: record.invoice_date || "",
        vehicle_no: record.vehicle_no || "",
        lr_no: record.lr_no || "",
        remarks: record.remarks || "",
      });
    } else {
      setForm({
        grn_date: today,

        vendor_id: "",
        vendor_label: "",

        warehouse_id: "",
        warehouse_label: "",

        po_id: "",
        po_label: "",

        invoice_no: "",
        invoice_date: "",
        vehicle_no: "",
        lr_no: "",
        remarks: "",
      });
    }
  }, [record, open]);

  

  // ----------------------------------------------------------
  // HANDLE NORMAL INPUT CHANGE
  // ----------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  // ----------------------------------------------------------
  // SAVE GRN HEADER
  // ----------------------------------------------------------
  const handleSave = async () => {
    try {

      // ---- BASIC VALIDATIONS ----
      if (!form.grn_date) {
        alert("Please select GRN Date");
        return;
      }

      if (!form.vendor_id) {
        alert("Please select Vendor");
        return;
      }

      if (!form.warehouse_id) {
        alert("Please enter Warehouse ID (Temporary)");
        return;
      }

      if (!form.po_id) {
        alert("Please select Purchase Order");
        return;
      }
      if (!form.invoice_date) {
        alert("Please select Invoice Date");
        return;
      }
      setSaving(true);

      const payload = {
        grn_date: form.grn_date,
        vendor_id: form.vendor_id,

        // send as integer
        warehouse_id: parseInt(form.warehouse_id),

        po_id: form.po_id,
        invoice_no: form.invoice_no,
        invoice_date: form.invoice_date,
        vehicle_no: form.vehicle_no,
        lr_no: form.lr_no,
        remarks: form.remarks,
      };

      await grnService.createGRN(payload);

      alert("GRN created successfully");

      if (onSaved) onSaved();

      onClose();

    } catch (error) {
      alert(
        error?.response?.data?.message ||
        "Failed to create GRN"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ----------------------------------------------------------
  // RENDER DRAWER UI
  // ----------------------------------------------------------
  return (
    <div className="erp-drawer-overlay">
      <div className="erp-drawer">

        <div className="erp-drawer-header">
          <h5>{isEdit ? "Edit GRN" : "Create GRN"}</h5>
          <button
            className="btn-close"
            onClick={onClose}
          />
        </div>

        <div className="erp-drawer-body">

          <div className="row g-3">

            {/* GRN DATE */}
            <div className="col-6">
              <label className="form-label">GRN Date</label>
              <input
                type="date"
                className="form-control"
                name="grn_date"
                value={form.grn_date}
                onChange={handleChange}
                 readOnly
              />
            </div>

            {/* VENDOR DROPDOWN */}
            <div className="col-6">
              <SearchableDropdown
                label="Vendor"
                value={form.vendor_label}
                placeholder="Search Vendor..."
                fetchOptions={vendorLookupService.search}
                getOptionLabel={(o) => `${o.bp_code} - ${o.bp_name}`}
                onChange={(v) =>
                  setForm({
                    ...form,
                    vendor_id: v.id,
                    vendor_label: `${v.bp_code} - ${v.bp_name}`,

                    // Reset PO when vendor changes
                    po_id: "",
                    po_label: "",
                  })
                }
              />
            </div>

            
            {/* WAREHOUSE */}
              <div className="col-6">
                <SearchableDropdown
                  label="Warehouse"
                  value={form.warehouse_label || ""}
                  placeholder="Search Warehouse..."
                  fetchOptions={warehouseLookupService.search}
                  getOptionLabel={(o) =>
                    `${o.warehouse_code} - ${o.warehouse_name}`
                  }
                  onChange={(v) =>
                    setForm({
                      ...form,
                      warehouse_id: v.id,
                      warehouse_label:
                        `${v.warehouse_code} - ${v.warehouse_name}`,
                    })
                  }
                />
              </div>

            {/* PURCHASE ORDER DROPDOWN */}
            <div className="col-6">
              <SearchableDropdown
                label="Purchase Order"
                value={form.po_label}
                placeholder={
                  form.vendor_id
                    ? "Search PO..."
                    : "Select Vendor First"
                }
                disabled={!form.vendor_id}
                fetchOptions={(search) =>
                  poLookupService.searchByVendor(
                    form.vendor_id,
                    search
                  )
                }
                getOptionLabel={(o) => o.po_no}
                onChange={(v) =>
                  setForm({
                    ...form,
                    po_id: v.id,
                    po_label: v.po_no,
                  })
                }
              />
            </div>

            {/* INVOICE NO */}
            <div className="col-6">
              <label className="form-label">Invoice No</label>
              <input
                type="text"
                className="form-control"
                name="invoice_no"
                value={form.invoice_no}
                onChange={handleChange}
              />
            </div>

            {/* INVOICE DATE */}
            <div className="col-6">
              <label className="form-label">Invoice Date<span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                name="invoice_date"
                value={form.invoice_date}
                onChange={handleChange}
              />
            </div>

            {/* VEHICLE NO */}
            <div className="col-6">
              <label className="form-label">Vehicle No</label>
              <input
                type="text"
                className="form-control"
                name="vehicle_no"
                value={form.vehicle_no}
                onChange={handleChange}
              />
            </div>

            {/* LR NO */}
            <div className="col-6">
              <label className="form-label">LR No</label>
              <input
                type="text"
                className="form-control"
                name="lr_no"
                value={form.lr_no}
                onChange={handleChange}
              />
            </div>

            {/* REMARKS */}
            <div className="col-12">
              <label className="form-label">Remarks</label>
              <textarea
                className="form-control"
                name="remarks"
                value={form.remarks}
                onChange={handleChange}
              />
            </div>

          </div>
        </div>

        <div className="erp-drawer-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

      </div>
    </div>
  );
}
